import React, { useState, useEffect } from "react";
import { X, Download, Share2, Copy, Check } from "lucide-react";
import QRCode from "qrcode";
import html2canvas from "html2canvas";
import VisitorQRView from "./VisitorQRView";
import { Button } from "./shared/Button";
import { toast } from "react-toastify";
import useAuthStore from "../store/useAuthStore";
import { marcaDe } from "../utils/marca";
import { llegadaDe, vieneEnVehiculo } from "../utils/llegada";
import { mostrarDia } from "../utils/fecha";
import api from "../api/axios";

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "1rem",
    backdropFilter: "blur(4px)",
  },
  modal: {
    maxWidth: "600px",
    width: "100%",
    backgroundColor: "var(--bg-elevated)",
    borderRadius: "var(--radius-xl)",
    boxShadow: "var(--shadow-2xl)",
    overflow: "hidden",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "var(--gradient-premium)",
    padding: "1.5rem",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    /* `color: inherit` a propósito.
     *
     * `src/index.css` tiene `h1, h2, …, h6 { color: var(--text) }`, y una
     * regla de hoja sobre el propio elemento gana a la herencia del
     * `color: white` que el encabezado pone en línea. En tema claro
     * `--text` es `#0A1628`, que es exactamente la primera parada del
     * degradado del encabezado: el título quedaba del mismo color que su
     * fondo, con contraste 1:1, invisible. El subtítulo no se veía afectado
     * porque `p` no lleva color en la hoja global. */
    color: "inherit",
    fontFamily: "var(--font-display)",
    fontSize: "var(--text-xl)",
    fontWeight: "700",
    margin: 0,
  },
  closeButton: {
    padding: "0.5rem",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "rgba(255, 255, 255, 0.2)",
    color: "white",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: "2rem",
    overflowY: "auto",
    flex: 1,
  },
  qrContainer: {
    textAlign: "center",
    marginBottom: "1.5rem",
  },
  qrImage: {
    maxWidth: "250px",
    width: "100%",
    height: "auto",
    margin: "0 auto",
    borderRadius: "var(--radius-lg)",
    border: "4px solid var(--gold)",
    padding: "1rem",
    backgroundColor: "white",
  },
  visitInfo: {
    backgroundColor: "var(--bg-secondary)",
    borderRadius: "var(--radius-lg)",
    padding: "1rem",
    marginBottom: "1.5rem",
    fontSize: "var(--text-sm)",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0.5rem 0",
    borderBottom: "1px solid var(--border)",
  },
  infoRowLast: {
    borderBottom: "none",
  },
  infoLabel: {
    color: "var(--text-secondary)",
  },
  infoValue: {
    fontWeight: "600",
    color: "var(--text)",
  },
  shareSection: {
    backgroundColor: "var(--gold-subtle)",
    borderRadius: "var(--radius-lg)",
    padding: "1rem",
    marginBottom: "1.5rem",
    border: "1px solid var(--gold)",
  },
  shareTitle: {
    fontSize: "var(--text-sm)",
    fontWeight: "600",
    color: "var(--text)",
    marginBottom: "0.75rem",
  },
  shareUrl: {
    display: "flex",
    gap: "0.5rem",
    marginBottom: "0.5rem",
  },
  urlInput: {
    flex: 1,
    padding: "0.5rem",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
    backgroundColor: "var(--bg)",
    fontSize: "var(--text-xs)",
    color: "var(--text)",
    fontFamily: "monospace",
  },
  actions: {
    display: "flex",
    gap: "0.75rem",
    flexWrap: "wrap",
  },
  note: {
    fontSize: "var(--text-sm)",
    color: "var(--text-secondary)",
    textAlign: "center",
    marginTop: "1rem",
    fontStyle: "italic",
  },
};

export default function VisitQRModal({ visit, onClose }) {
  const [copied, setCopied] = useState(false);
  /* ARRANCA EN `false`, Y ES LA DECISIÓN QUE IMPORTA.
   *
   * Esta pantalla preguntaba por `/system-config`, que es SÓLO DE
   * ADMINISTRADOR. A un residente le devuelve 403, el `catch` se lo traga, y
   * el valor inicial se quedaba puesto: el pase afirmaba «Se envió un SMS a
   * 7777-1234 con el enlace al código QR» con el SMS DESACTIVADO en la
   * residencial. El servidor sí respeta el interruptor, así que la frase era
   * sencillamente falsa — y encima aparecía un aviso de «No tienes permisos»
   * que acusaba al residente de algo que no había hecho.
   *
   * Entre afirmar un envío que quizá no ocurrió y callarlo, se calla. */
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const { user } = useAuthStore();

  const shareUrl =
    visit.qrUrl ||
    `${window.location.origin}/v/${visit.qrCode?.substring(0, 8)}`;

  useEffect(() => {
    const fetchSmsConfig = async () => {
      /* Sólo lo pregunta quien puede: pedirlo como residente devolvía 403 y
       * un aviso de «No tienes permisos» en pantalla, por una consulta que el
       * usuario nunca pidió hacer. */
      if (user?.role !== "admin" && user?.role !== "superadmin") {
        setLoadingConfig(false);
        return;
      }
      try {
        const response = await api.get('/system-config');
        const smsConfig = response.data.configs.notifications?.find(
          config => config.key === 'sms_enabled'
        );
        if (smsConfig) {
          setSmsEnabled(smsConfig.value);
        }
      } catch (error) {
        console.error('Error fetching SMS config:', error);
      } finally {
        setLoadingConfig(false);
      }
    };
    
    fetchSmsConfig();
  }, []);

  const downloadQR = async () => {
    try {
      // Buscar el elemento VisitorQRView dentro del modal
      const visitorQRElement = document.getElementById('visitor-qr-view-modal');
      
      if (!visitorQRElement) {
        toast.error("No se pudo encontrar la vista para descargar");
        return;
      }

      // Buscar el card interno (el contenido real sin márgenes)
      const cardElement = visitorQRElement.querySelector('div[style*="maxWidth"]');
      const elementToCapture = cardElement || visitorQRElement;

      // Capturar la vista completa usando html2canvas
      const canvas = await html2canvas(elementToCapture, {
        backgroundColor: null, // Transparente
        scale: 2, // Mayor calidad
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: elementToCapture.scrollWidth,
        height: elementToCapture.scrollHeight,
        windowWidth: elementToCapture.scrollWidth,
        windowHeight: elementToCapture.scrollHeight,
        x: 0,
        y: 0
      });

      // Convertir el canvas a blob
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error("Error al generar la imagen");
          return;
        }

        // Crear enlace de descarga
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `visita-${visit.visitorName.replace(/\s+/g, "-")}-completa.png`;
        link.href = url;
        link.click();
        
        // Limpiar
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error("Error downloading complete view:", error);
      toast.error("Error al descargar la vista completa");
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Enlace copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Error al copiar el enlace");
    }
  };

  const shareViaWhatsApp = () => {
    /* `mostrarDia`, y NO la zona de El Salvador. La fecha se escogió en un
       `<input type="date">` y el API la devuelve como medianoche UTC, así que
       convertirla a UTC-6 restaba un día. De todos los sitios donde pasaba,
       éste es el que más duele: este texto sale del sistema por WhatsApp y lo
       lee el visitante, que es justo quien se presentaría el día equivocado. */
    const dateStr = mostrarDia(visit.date, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });

    const residentName = user?.name || visit.createdBy?.name || "Residente";

    /* EL NOMBRE SALE DE LA SESIÓN, NO DEL CÓDIGO.
     *
     * Aquí decía «VILLA PRIMAVERA» escrito a mano, para todas las
     * residenciales. Es el mensaje que el residente le reenvía a su
     * visitante, o sea lo primero que ve alguien de fuera: mandarle el
     * nombre de otra comunidad es la clase de detalle que hace dudar de
     * todo lo demás. El backend ya lo resolvió así para el SMS. */
    const residencial = marcaDe(user).name;

    /* La forma de llegada sólo se menciona si el visitante viene en algo: en
     * una visita a pie, un renglón que diga «A pie» no le aporta nada a
     * nadie. Cuando hay placa, sí se pone, para que la compruebe antes de
     * llegar a la caseta y no ahí, con la fila detrás. */
    const lineaLlegada = vieneEnVehiculo(visit)
      ? `\n\u{1F697} Llega en: ${llegadaDe(visit).etiqueta.replace(/^En /, "")}${
          visit.plate ? `, placa ${visit.plate}` : ""
        }`
      : "";

    const message = `🏡 ${residencial} — Confirmación de visita

Estimado(a) ${visit.visitorName},

Su visita quedó autorizada:
📅 Fecha: ${dateStr}
📍 Destino: Polígono ${visit.polygon}, Casa ${visit.houseNumber}
👤 Le espera: ${residentName}${lineaLlegada}

Éste es su pase de entrada. Muéstrelo en la caseta al llegar:
${shareUrl}

Tenga en cuenta:
• El pase es personal: no lo reenvíe a nadie más
• Corresponde a la fecha indicada arriba
• Puede mostrarlo desde el mismo teléfono, no hace falta imprimirlo

¡Le esperamos!
${residencial}`;

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  if (!visit.qrCode || !visit.qrToken) {
    return null; // No mostrar modal si no hay QR
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Código QR de Visita</h2>
          <button
            onClick={onClose}
            style={styles.closeButton}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        <div style={styles.content}>
          {/* Mostrar la vista completa del visitante */}
          <div style={{ marginBottom: "1.5rem" }} id="visitor-qr-view-modal">
            <VisitorQRView visit={visit} isModal={true} />
          </div>

          <div style={styles.shareSection}>
            <div style={styles.shareTitle}>Compartir con el visitante:</div>
            <div style={styles.shareUrl}>
              <input
                type="text"
                value={shareUrl}
                readOnly
                style={styles.urlInput}
                onClick={(e) => e.target.select()}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={copyToClipboard}
                leftIcon={copied ? Check : Copy}
              >
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </div>
          </div>

          <div style={styles.actions}>
            <Button
              variant="primary"
              onClick={shareViaWhatsApp}
              leftIcon={Share2}
              fullWidth
            >
              Compartir por WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={downloadQR}
              leftIcon={Download}
              fullWidth
            >
              Descargar QR
            </Button>
          </div>

          <p style={styles.note}>
            {visit.visitorPhone && smsEnabled && !loadingConfig
              ? `Se envió un SMS a ${visit.visitorPhone} con el enlace al código QR`
              : "Comparte este código QR con tu visitante para que pueda acceder"}
          </p>
        </div>
      </div>
    </div>
  );
}
